/* eslint-disable react/jsx-handler-names */
import { type FC, type PropsWithChildren, type RefObject, useRef, useState } from 'react';
import loadable from '@loadable/component';

import { type CartType } from '@jsmdg/cart-library-ts';
import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    ClickElement,
    ClickText,
    ClickType,
    trackClickGoToCheckoutButton,
    trackCloseCartOverlay,
    trackShowCartOverlay,
} from '@jsmdg/tracking';
import { createCustomUIEvent, CustomUIEventEnum } from '@jsmdg/ui-event';
import { Breakpoint, useBreakpoint } from '@jsmdg/yoshi';
import { OverlayContext } from '../contexts';
import { type AddToCartClickParams, type HandleAddToCartClick } from '../hooks/useBuybox';
import { FeatureFlagVariant } from '../types/enums/featureFlagVariant';
import { type ProductDetailContext } from '../types/types';

const OverlayCart = loadable(async () =>
    import('../components/CartSelectionOverlay/OverlayCart/OverlayCart').then(
        module => module.OverlayCart,
    ),
);

type AddToCartWithOverlayArgs = {
    currentItemId?: string;
    handleAddToCartClick: HandleAddToCartClick;
    isCheckoutOverlayDisabled: boolean;
    isCheckoutProcessInitiated: RefObject<boolean>;
    isMobile: boolean;
    onCartAdded: (cart: CartType, itemId: string) => void;
    onOverlayOpen: () => void;
    onCheckoutInitialized: () => void;
    onCheckoutEnded: () => void;
    params: AddToCartClickParams;
};

async function addToCartWithOverlay({
    currentItemId,
    handleAddToCartClick,
    isCheckoutOverlayDisabled,
    isCheckoutProcessInitiated,
    isMobile,
    onCartAdded,
    onCheckoutEnded,
    onCheckoutInitialized,
    onOverlayOpen,
    params,
}: AddToCartWithOverlayArgs): Promise<void> {
    if (isCheckoutProcessInitiated.current) {
        return;
    }

    if (!isMobile || isCheckoutOverlayDisabled) {
        await handleAddToCartClick({ ...params, withNavigateToCart: true });
        return;
    }

    onCheckoutInitialized();

    try {
        onOverlayOpen();

        const result = await handleAddToCartClick({ ...params, withNavigateToCart: false });
        const cart = result?.cart;
        const itemId = result?.currentItemId;

        if (itemId && cart) {
            onCartAdded(cart, itemId);
        }
    } catch (error) {
        logToSentry(error as Error, {
            currentItemId: currentItemId ?? '',
            clickType: params.clickType ?? '',
        });
        await handleAddToCartClick({ ...params, withNavigateToCart: true });
    } finally {
        onCheckoutEnded();
        document.dispatchEvent(createCustomUIEvent(CustomUIEventEnum.FetchCart));
    }
}

export const OverlayProvider: FC<PropsWithChildren> = ({ children }) => {
    const [showCartSelectionOverlay, setShowCartSelectionOverlay] = useState(false);
    const [currentItemId, setCurrentItemId] = useState<string>();
    const [initialCart, setInitialCart] = useState<CartType | null>(null);
    const isCheckoutProcessInitiated = useRef(false);
    const isMobile = !useBreakpoint(Breakpoint.SM);
    const { featureFlagsVariation } = useFragmentContext<ProductDetailContext>();

    const isCheckoutOverlayDisabled = Boolean(
        featureFlagsVariation?.checkout_overlay_enabled !== FeatureFlagVariant.ON,
    );

    const activateCheckoutProcess = () => (isCheckoutProcessInitiated.current = true);

    const deactivateCheckoutProcess = () => (isCheckoutProcessInitiated.current = false);

    const wrapAddToCartWithOverlay = (
        handleAddToCartClick: HandleAddToCartClick,
        params: AddToCartClickParams,
    ): Promise<void> =>
        addToCartWithOverlay({
            currentItemId,
            handleAddToCartClick,
            isCheckoutOverlayDisabled,
            isCheckoutProcessInitiated,
            isMobile,
            onCartAdded: (cart, itemId) => {
                setInitialCart(cart);
                setCurrentItemId(itemId);
            },
            onCheckoutInitialized: activateCheckoutProcess,
            onCheckoutEnded: deactivateCheckoutProcess,
            onOverlayOpen: () => {
                setShowCartSelectionOverlay(true);
                trackShowCartOverlay(ClickElement.OpenViaPDP);
            },
            params,
        });

    const contextValue = {
        isOpen: showCartSelectionOverlay,
        onRequestClose: async (): Promise<void> => {
            setShowCartSelectionOverlay(false);
            trackCloseCartOverlay(
                ClickElement.CloseViaXButton,
                ClickText.Close,
                ClickType.ClosePDPOverlay,
            );
        },
        onContinueShopping: (): void => {
            setShowCartSelectionOverlay(false);
            trackCloseCartOverlay(
                ClickElement.CloseViaContinueShoppingButton,
                ClickText.ContinueShopping,
                ClickType.ClosePDPOverlay,
            );
        },
        onCheckout: (): void => {
            sessionStorage.setItem('purchase-from-overlay', 'true');
            trackClickGoToCheckoutButton();
            setShowCartSelectionOverlay(false);
            window.location.assign('/checkout');
        },
        wrapAddToCartWithOverlay,
        initialCart,
        closeCartSelectionOverlay: (): void => {
            setShowCartSelectionOverlay(false);
        },
        itemId: currentItemId,
    };

    return (
        <OverlayContext value={contextValue}>
            {children}
            {isMobile && showCartSelectionOverlay && (
                <OverlayCart
                    isOpen={showCartSelectionOverlay}
                    onRequestClose={contextValue.onRequestClose}
                    onContinueShopping={contextValue.onContinueShopping}
                    onCheckout={contextValue.onCheckout}
                    initialCart={initialCart}
                    itemId={currentItemId}
                />
            )}
        </OverlayContext>
    );
};
